import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';
import { Message, Table } from 'semantic-ui-react';

import Search from '../views/Shared/Search';
import withPagination from './WithPagination';
import LoadingDataEnhancer from './LoadingData';
import CancelledGuaranteePurchasesV2 from './CancelledGuaranteePurchasesV2';
import GuaranteePurchasesList from '../views/CancelledGuaranteePurchases/List';
import GuaranteeReinstate from '../views/CancelledGuaranteePurchases/ReinstateGuaranteeForm';
import cancelledPurchasesQuery from '../graphql/cancelledPurchasesQuery';
import { setSearch } from '../data/searchField/actions';
import { setPage } from '../data/page/actions';
import reinstateGuaranteePurchaseMutation from '../graphql/reinstateGuaranteePurchaseMutation';
import AuditModal from '../components/AuditModal';
import cancelledGuaranteePurchasesAuditsQuery from '../graphql/cancelledGuaranteePurchasesAuditsQuery';
import { useGuaranteesApiV2 } from '../hooks/useGuaranteePurchases';

const CancelledGuaranteePurchases = ({
  data: { cancelledPurchases = [], refetch },
  searchFields,
  setSearchFields: setSearchFieldsAction,
  setPage: setPageAction,
  reinstateMutation,
}) => {
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);

  const [selectedGuaranteePurchase, setSelectedGuaranteePurchase] = useState(null);
  const [isReinstateModalOpen, setIsReinstateModalOpen] = useState(false);
  const [reinstateFormError, setReinstateFormError] = useState(null);
  const [reinstateFormSubmitting, setReinstateFormSubmitting] = useState(false);
  const [flashMessage, setFlashMessage] = useState(null);

  const handleMessageDismiss = useCallback(() => {
    setFlashMessage(null);
  }, []);

  const onSelectGuarantee = useCallback((action, guarantee) => {
    setSelectedGuaranteePurchase(guarantee);

    switch (action) {
      case 'Reinstate Guarantee':
        setIsReinstateModalOpen(true);
        setReinstateFormError(null);
        setReinstateFormSubmitting(false);
        break;
      case 'Status History':
        setIsAuditModalOpen(true);
        break;
      default:
        setSelectedGuaranteePurchase(null);
    }
  }, []);

  const onSearchSubmitHandler = useCallback(
    event => {
      const { vin, dealerNumber } = event.target;
      const variableArguments = {
        vin: vin.value.trim() || null,
        dealerNumber: dealerNumber.value.trim() || null,
      };
      refetch({
        ...variableArguments,
      });

      setSearchFieldsAction(variableArguments);
      setPageAction({});
    },
    [refetch, setSearchFieldsAction, setPageAction]
  );

  const onAuditCloseHandler = useCallback(() => {
    setIsAuditModalOpen(false);
    setSelectedGuaranteePurchase(null);
  }, []);

  const onReinstateFormCloseHandler = useCallback(() => {
    setIsReinstateModalOpen(false);
    setSelectedGuaranteePurchase(null);
  }, []);

  const onReinstateFormSubmitHandler = useCallback(
    values => {
      setReinstateFormSubmitting(true);

      const reinstateInput = { ...values };
      reinstateMutation({
        variables: { reinstateInput },
      })
        .then(({ data: mutationData }) => {
          const {
            reinstateGuaranteePurchase: { success, errors },
          } = mutationData;
          if (success) {
            refetch();
            setIsReinstateModalOpen(false);
            setSelectedGuaranteePurchase(null);
            setReinstateFormSubmitting(false);
            setFlashMessage('Successfully Reinstate Guarantee Purchase');
          } else {
            setReinstateFormSubmitting(false);
            setReinstateFormError(errors);
          }
        })
        .catch(() => {
          setReinstateFormSubmitting(false);
          setReinstateFormError('Error while reinstate Guarantee Purchase');
        });
    },
    [reinstateMutation, refetch]
  );

  const renderAuditRow = auditRecord => {
    return (
      <>
        {auditRecord.event === 'update'
          && `${auditRecord.versionAuthor} updated the Cancelled Guarantee - ${
            auditRecord.itemType
          } details`}
        {auditRecord.event === 'create'
          && `${auditRecord.versionAuthor} created the Cancelled Guarantee - ${
            auditRecord.itemType
          }`}
        {auditRecord.event === 'destroy'
          && `${auditRecord.versionAuthor} Destroyed Cancelled Guarantee - ${
            auditRecord.itemType
          }`}
      </>
    );
  };

  const renderChangesetRow = (auditRecord, change, key) => {
    if (key !== 'updated_at'
      && auditRecord.event === 'update'
      && change[1] !== null
      && change[1] !== '') {
      return (
        <Table.Row key={key}>
          <Table.Cell>
            <span>
              set{' '}
              <span style={{ fontWeight: 'bold' }}>
                {key}{' '}
              </span>
              from{' '}
              <span style={{ fontWeight: 'bold' }}>
                {' '}
                {change[0] || 'nil'}{' '}
              </span>
              to{' '}
              <span style={{ fontWeight: 'bold' }}>
                {change[1] || 'nil'}{' '}
              </span>
            </span>
          </Table.Cell>
        </Table.Row>
      );
    }
    return null;
  };

  return (
    <div>
      {flashMessage && (
        <Message
          onDismiss={handleMessageDismiss}
          content={flashMessage}
          positive
        />
      )}
      <Search
        onSubmit={onSearchSubmitHandler}
        searchFields={searchFields}
        searchOptions={['vin', 'dealerNumber']}
      />
      <GuaranteePurchasesList
        onSelectGuarantee={onSelectGuarantee}
        cancelledPurchases={cancelledPurchases}
      />
      { isAuditModalOpen && selectedGuaranteePurchase && (
        <AuditModal
          header='Status History - Canceled Purchases'
          fetchAuditRecordQuery={cancelledGuaranteePurchasesAuditsQuery}
          queryVariables={{ cancelledPurchaseId: parseInt(selectedGuaranteePurchase.id, 10) }}
          auditResponseAttribute='cancelledPurchaseAudits'
          renderAuditRow={renderAuditRow}
          renderChangesetRow={renderChangesetRow}
          isAuditModalOpen={isAuditModalOpen}
          onCloseHandler={onAuditCloseHandler}
        />
      )}
      {selectedGuaranteePurchase && isReinstateModalOpen && (
        <div>
          <GuaranteeReinstate
            isReinstateModalOpen={isReinstateModalOpen}
            onCloseHandler={onReinstateFormCloseHandler}
            onSubmitHandler={onReinstateFormSubmitHandler}
            cancelledPurchase={selectedGuaranteePurchase}
            error={reinstateFormError}
            submitting={reinstateFormSubmitting}
          />
        </div>
      )}
    </div>
  );
};

CancelledGuaranteePurchases.propTypes = {
  data: PropTypes.shape({
    cancelledPurchases: PropTypes.array,
    refetch: PropTypes.func,
  }),
  searchFields: PropTypes.object.isRequired,
  setSearchFields: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  reinstateMutation: PropTypes.func,
};

CancelledGuaranteePurchases.defaultProps = {
  data: {},
  reinstateMutation: null,
};

const mapDispatchToProps = dispatch => ({
  setSearchFields: fields => {
    dispatch(setSearch(fields));
  },
  setPage: () => {
    dispatch(setPage());
  },
});

const mapStateToProps = state => {
  const {
    data: { searchFields },
  } = state;
  return {
    searchFields,
  };
};

const V1Container = compose(
  withRouter,
  withPagination(cancelledPurchasesQuery, 'cancelledPurchases'),
  graphql(reinstateGuaranteePurchaseMutation, {
    name: 'reinstateMutation',
  }),
  LoadingDataEnhancer,
  connect(mapStateToProps, mapDispatchToProps)
)(CancelledGuaranteePurchases);

const CancelledGuaranteePurchasesContainer = props => {
  const [isApiV2Enabled, loaded] = useGuaranteesApiV2();
  if (!loaded) return null;

  return React.createElement(
    isApiV2Enabled ? CancelledGuaranteePurchasesV2 : V1Container,
    props
  );
};

export default CancelledGuaranteePurchasesContainer;
